import { and, eq } from "drizzle-orm";
import {
  adminNotifications,
  type AdminNotificationAudience,
  type AdminNotificationSeverity,
} from "@/server/platform/db/schema/notifications";
import type { DbTransaction } from "@/shared/types";

export type EmitAdminNotificationInput = {
  type: string;
  title: string;
  body?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  href: string;
  severity?: AdminNotificationSeverity;
  audience?: AdminNotificationAudience;
  requiredPermission?: string | null;
  createdAt?: Date;
  expiresAt?: Date | null;
};

export async function emitAdminNotification(
  input: EmitAdminNotificationInput,
  db: DbTransaction,
): Promise<string | null> {
  const now = input.createdAt ?? new Date();
  const hasEntity = input.entityType && input.entityId;

  const values = {
    type: input.type,
    title: input.title,
    body: input.body ?? null,
    entityType: input.entityType ?? null,
    entityId: input.entityId ?? null,
    href: input.href,
    severity: input.severity ?? "normal",
    audience: input.audience ?? "permission",
    requiredPermission: input.requiredPermission ?? null,
    createdAt: now,
    expiresAt: input.expiresAt ?? null,
  };

  if (hasEntity) {
    const [existing] = await db
      .select({ id: adminNotifications.id })
      .from(adminNotifications)
      .where(
        and(
          eq(adminNotifications.type, input.type),
          eq(adminNotifications.entityType, input.entityType!),
          eq(adminNotifications.entityId, input.entityId!),
        ),
      )
      .limit(1);

    if (existing?.id) return existing.id;

    const [inserted] = await db
      .insert(adminNotifications)
      .values(values)
      .onConflictDoNothing({
        target: [
          adminNotifications.type,
          adminNotifications.entityType,
          adminNotifications.entityId,
        ],
      })
      .returning({ id: adminNotifications.id });

    if (inserted?.id) return inserted.id;

    const [afterRace] = await db
      .select({ id: adminNotifications.id })
      .from(adminNotifications)
      .where(
        and(
          eq(adminNotifications.type, input.type),
          eq(adminNotifications.entityType, input.entityType!),
          eq(adminNotifications.entityId, input.entityId!),
        ),
      )
      .limit(1);

    return afterRace?.id ?? null;
  }

  const [inserted] = await db
    .insert(adminNotifications)
    .values(values)
    .returning({ id: adminNotifications.id });

  return inserted?.id ?? null;
}
